// USSD and SMS PIN (FR-81): the PIN protects actions taken by dialling the
// brand's short code or texting keywords. Saving it needs step-up "ussd_pin".
import { Button, Code, Group, List, Paper, PasswordInput, Stack, Text, Title } from "@mantine/core";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client";
import { useBrand } from "../../shared/brand/brand";
import { errorMessage } from "../../shared/session/errors";
import { useStepUp } from "../../shared/session/stepup";
import { FormError, Notice } from "../../shared/ui/Page";
import { pinProblem, type PinProblem } from "./pin";

const digitsOnly = (v: string) => v.replace(/\D/g, "").slice(0, 6);

export function UssdPinSection() {
  const { t } = useTranslation();
  const brand = useBrand();
  const stepUp = useStepUp();
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [problem, setProblem] = useState<PinProblem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const code = brand.ussd_short_code;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    const p = pinProblem(pin, confirm);
    setProblem(p);
    if (p) return;
    const token = await stepUp("ussd_pin");
    if (!token) return;
    setBusy(true);
    const { response, error } = await api.POST("/me/ussd-pin", {
      params: { header: { "X-Step-Up-Token": token } },
      body: { pin },
    });
    setBusy(false);
    if (!response.ok) return setError(errorMessage(error, t("error.generic")));
    setPin("");
    setConfirm("");
    setNotice(t("ussd_pin.saved", { code }));
  };

  const keywords: [string, string][] = [
    [t("ussd_pin.kw_code"), t("ussd_pin.kw_code_desc")],
    [t("ussd_pin.kw_consents"), t("ussd_pin.kw_consents_desc")],
    [t("ussd_pin.kw_revoke"), t("ussd_pin.kw_revoke_desc")],
  ];

  return (
    <Paper withBorder p="md" radius="md" component="section" aria-labelledby="ussd-pin-title">
      <Stack gap="md">
        <Title order={2} size="h4" id="ussd-pin-title">
          {t("ussd_pin.title")}
        </Title>
        <Text size="sm" data-testid="ussd-pin-intro">
          {t("ussd_pin.intro", { code })}
        </Text>
        <Stack gap={4}>
          <Text size="sm" fw={600}>
            {t("ussd_pin.keywords_title")}
          </Text>
          <List size="sm" spacing={2}>
            {keywords.map(([kw, desc]) => (
              <List.Item key={kw}>
                <Code>{kw}</Code> – {desc}
              </List.Item>
            ))}
          </List>
        </Stack>
        <Text size="sm" c="dimmed">
          {t("ussd_pin.first_use", { code })}
        </Text>
        {notice && <Notice>{notice}</Notice>}
        <form onSubmit={save} noValidate>
          <Stack gap="sm" maw={320}>
            <PasswordInput
              label={t("ussd_pin.new")}
              description={t("ussd_pin.hint")}
              inputMode="numeric"
              autoComplete="new-password"
              maxLength={6}
              value={pin}
              onChange={(e) => setPin(digitsOnly(e.currentTarget.value))}
              error={problem === "err_format" || problem === "err_trivial" ? t(`ussd_pin.${problem}`) : undefined}
              required
            />
            <PasswordInput
              label={t("ussd_pin.confirm")}
              inputMode="numeric"
              autoComplete="new-password"
              maxLength={6}
              value={confirm}
              onChange={(e) => setConfirm(digitsOnly(e.currentTarget.value))}
              error={problem === "err_mismatch" ? t("ussd_pin.err_mismatch") : undefined}
              required
            />
            <FormError message={error} />
            <Group>
              <Button type="submit" loading={busy}>
                {t("ussd_pin.save")}
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Paper>
  );
}
